import { useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import axios from "axios";
import { USER_API } from "config";
import { handleToast } from "utils/toasts";
import { status } from "store/types/enum";
import { buildAuthMutationHeaders } from "utils/authTurnstileRequest";

const formatRateLimitTime = (date: Date): string => {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  if (diffMs <= 0) return "0:00";

  const totalMinutes = Math.ceil(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  }
  return `${minutes}:00`;
};

interface ResendCancelDeletionResponse {
  message?: string;
}

export const useResendCancelDeletionEmail = () => {
  const [rateLimitUntil, setRateLimitUntil] = useState<Date | null>(null);

  useEffect(() => {
    if (!rateLimitUntil) return;
    const timer = setInterval(() => {
      const now = new Date();
      if (now >= rateLimitUntil) {
        setRateLimitUntil(null);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [rateLimitUntil]);

  const mutation = useMutation({
    mutationFn: async (input: {
      email: string;
      turnstileHeaders?: Record<string, string>;
    }): Promise<ResendCancelDeletionResponse> => {
      const response = await axios.post<ResendCancelDeletionResponse>(
        USER_API.resendCancelDeletionEmail,
        { email: input.email },
        { headers: buildAuthMutationHeaders(input.turnstileHeaders) }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setRateLimitUntil(null);
      handleToast(data?.message ?? "Cancel-deletion link sent. Check your email.", status.SUCCESS);
    },
    onError: (error: {
      response?: { data?: { code?: string; retry_after?: string; message?: string } };
    }) => {
      const data = error?.response?.data;
      const code = data?.code;
      const retryAfter = data?.retry_after;
      if (code === "RATE_LIMITED" && retryAfter) {
        const retryAfterDate = new Date(retryAfter);
        setRateLimitUntil(retryAfterDate);
        handleToast(
          `Too many attempts. Please try again in ${formatRateLimitTime(retryAfterDate)}.`,
          status.ERROR
        );
        return;
      }
      const message =
        (data as { message?: string })?.message ?? "Failed to send email. Please try again later.";
      handleToast(message, status.ERROR);
    },
  });

  const isRateLimited = rateLimitUntil ? rateLimitUntil > new Date() : false;

  return {
    ...mutation,
    rateLimitUntil,
    formatRateLimitTime,
    isRateLimited,
  };
};
